import React from 'react';
import ProgressSidebar from './ProgressSidebar';
import BrandLogo from './BrandLogo';

/**
 * Shell layout for the wizard.
 *
 * Renders the sidebar on large screens and a mobile progress bar on small
 * screens, plus the main content area with Back / Next navigation buttons.
 *
 * @param {number}   currentStep
 * @param {Array}    steps        - Step descriptor array (from WizardPage).
 * @param {Set}      hidden       - Indices of steps the client doesn't see
 *                                  (left out of the sidebar and the count).
 * @param {function} onBack       - Navigate to previous step.
 * @param {function} onNext       - Navigate to next step (or submit).
 * @param {boolean}  isFirst      - Hide the Back button on the first step.
 * @param {boolean}  isLast       - Show a Submit button on the final step.
 * @param {boolean}  saving       - Show spinner on the Next/Submit button.
 * @param {function} onStepClick  - Jump to a previous step via the sidebar.
 * @param {string}   clientName
 * @param {function} onLogout
 */
export default function WizardLayout({
  currentStep,
  steps,
  hidden = new Set(),
  onBack,
  onNext,
  onSkip,
  showSkip,
  isFirst,
  isLast,
  saving,
  onStepClick,
  stepStatuses,
  clientName,
  onLogout,
  children,
}) {
  // Count only the steps the client sees; Welcome (the first) isn't counted.
  const visible = steps.map((_, idx) => idx).filter((idx) => !hidden.has(idx));
  const progressSteps = visible.length - 1;
  const position = Math.max(0, visible.indexOf(currentStep));
  const pct = position === 0 ? 0 : Math.round((position / progressSteps) * 100);

  return (
    <div className="cashflow-wizard-shell d-flex vh-100">
      {/* Sidebar */}
      <ProgressSidebar
        currentStep={currentStep}
        steps={steps}
        hidden={hidden}
        onStepClick={onStepClick}
        stepStatuses={stepStatuses}
      />

      {/* Main area */}
      <div className="cashflow-main flex-fill d-flex flex-column overflow-hidden">
        {/* Top bar */}
        <header className="cashflow-topbar d-flex align-items-center px-4 py-2 border-bottom">
          {/* Mobile: show triangle logo */}
          <span className="d-lg-none me-2"><BrandLogo size={28} /></span>
          <span className="fw-semibold cashflow-brand-text d-lg-none me-auto small">
            Cashflow Analysis
          </span>
          <span className="text-muted small ms-auto d-none d-lg-block">
            Logged in as <strong>{clientName}</strong>
          </span>
          <button
            className="btn btn-link btn-sm text-muted ms-3 p-0"
            onClick={onLogout}
            title="Sign out"
          >
            <i className="bi bi-box-arrow-right" />
          </button>
        </header>

        {/* Mobile progress bar */}
        <div className="d-lg-none px-3 pt-2">
          <div className="d-flex justify-content-between small text-muted mb-1">
            <span>
              {position === 0 ? 'Welcome' : `Step ${position} of ${progressSteps}`}
            </span>
            <span>{steps[currentStep]?.label}</span>
          </div>
          <div className="progress" style={{ height: 6 }}>
            <div
              className="progress-bar bg-danger"
              style={{ width: `${pct}%` }}
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>

        {/* Step content (scrollable) */}
        <main className="cashflow-step-content flex-fill overflow-auto px-4 py-4">
          {children}
        </main>

        {/* Navigation footer */}
        <footer className="cashflow-footer border-top px-4 py-3 d-flex justify-content-between align-items-center">
          <button
            className="btn btn-outline-secondary"
            onClick={onBack}
            disabled={isFirst || saving}
          >
            <i className="bi bi-arrow-left me-1" />
            Back
          </button>

          {/* Progress bar with step count — truly centered by flex */}
          <div className="flex-fill mx-3" style={{ maxWidth: 400 }}>
            <div className="position-relative">
              <div className="progress" style={{ height: 22, borderRadius: 12 }}>
                <div
                  className="progress-bar bg-danger"
                  style={{ width: `${pct}%`, borderRadius: 12, transition: 'width 0.3s ease' }}
                  role="progressbar"
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <span
                className="position-absolute top-50 start-50 translate-middle fw-semibold"
                style={{ fontSize: '0.75rem', color: pct > 45 ? '#fff' : '#666', zIndex: 1 }}
              >
                {position === 0 ? 'Welcome' : `${position} / ${progressSteps}`}
              </span>
            </div>
          </div>

          {/* Right-side button group — skip + next stay together */}
          <div className="d-flex gap-2">
            {showSkip && !isLast && (
              <button
                className="btn btn-outline-secondary px-3"
                onClick={onSkip}
                disabled={saving}
                title="I reviewed this page — nothing to enter"
              >
                <i className="bi bi-check2 me-1" />
                Nothing to enter
              </button>
            )}

            <button
              className={`btn ${isLast ? 'btn-success' : 'btn-danger'} px-4`}
              onClick={onNext}
              disabled={saving}
            >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : isLast ? (
              <>
                Submit <i className="bi bi-check-lg ms-1" />
              </>
            ) : (
              <>
                Next <i className="bi bi-arrow-right ms-1" />
              </>
            )}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
